// Copy and small pure helpers for the interest fit confirmation. Kept apart
// from the component so tests can pin the words without rendering.
//
// House rules: sentence case, second person, no em dashes.

export const OUTCOME_WORDS = Object.freeze({
  match: 'Fits',
  partial: 'Partly',
  mismatch: 'Differs',
  unknown: 'Not compared',
});

/** Rows a person must read before confirming, in display order. */
export const DIFFERENCE_OUTCOMES = Object.freeze(['mismatch', 'partial']);

export function sideLabels(audience) {
  return audience === 'company'
    ? { listing: 'Your listing', provider: 'This provider' }
    : { listing: 'This job', provider: 'You' };
}

export function headerCopy(audience) {
  return audience === 'company'
    ? {
        eyebrow: 'Before you connect',
        title: 'Confirm this connection',
      }
    : {
        eyebrow: 'Before you express interest',
        title: 'Confirm your interest',
      };
}

export function loadingCopy(audience) {
  return audience === 'company'
    ? 'Comparing this provider with your listing...'
    : 'Comparing this job with your preferences...';
}

export function unavailableCopy(audience) {
  return audience === 'company'
    ? 'We could not compare this provider with your listing right now. You can still confirm the connection.'
    : 'We could not compare this job with your preferences right now. You can still confirm your interest.';
}

export function cancelLabel(audience) {
  return audience === 'company' ? 'Not this provider' : 'Not for me';
}

export function confirmLabel(audience, differenceCount) {
  if (audience === 'company') {
    return differenceCount > 0 ? 'Connect anyway' : 'Yes, connect';
  }
  return differenceCount > 0 ? 'Still interested' : "Yes, I'm interested";
}

/** One sentence above the rows. */
export function summaryCopy(audience, report) {
  const differences = countDifferences(report);
  const matches = report?.matches?.length ?? 0;
  if (differences > 0) {
    const noun = differences === 1 ? 'thing differs' : 'things differ';
    const them = differences === 1 ? 'it' : 'them';
    return audience === 'company'
      ? `${differences} ${noun} from what this provider is looking for. Read ${them} before you connect.`
      : `${differences} ${noun} from your preferences. Read ${them} before you continue.`;
  }
  if (matches > 0) return 'Everything we could compare lines up.';
  // Either side may be the sparse one (most all-unknown reports come from a
  // listing that states nothing), so the copy blames neither.
  return 'We could not compare much yet. Neither side has filled in enough details.';
}

export function countDifferences(report) {
  if (!report) return 0;
  return (
    (report.mismatches?.length ?? 0) + (report.partials?.length ?? 0)
  );
}

/** Rows grouped for display: differences first, then matches, then the rest. */
export function groupRows(report) {
  const rows = Array.isArray(report?.rows) ? report.rows : [];
  return {
    differences: rows.filter((r) => DIFFERENCE_OUTCOMES.includes(r.outcome)),
    matches: rows.filter((r) => r.outcome === 'match'),
    unknowns: rows.filter((r) => r.outcome === 'unknown'),
  };
}

/** "CRNA · AVEA CRNA · Louisville, KY" from whatever is known. */
export function subjectLine(subject) {
  if (!subject) return '';
  return [subject.title, subject.facilityName, subject.location]
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter(Boolean)
    .join(' · ');
}
